import {
  Autocomplete,
  Link,
  DialogTitle,
  DialogActions,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { useEffect, useRef, useState } from "react";
import {
  roomHasIntegrationToken,
  type PremiseConfig,
  type Unit,
} from "@shared/types/foyer";
import { foyerApi, type RoomTypeRecord } from "../http/foyer";
import { orientationOfResolution, rotationMismatch } from "@shared/media";
import {
  BUILTIN_ROOM_TYPES,
  DOOR_SIGN_ROOM_TYPE,
  isSignageRoomType,
  roomTypesForPremise,
  type BuiltinRoomType,
} from "@shared/builtinRoomTypes";
import { EditDrawer, ItemFormSurface, type Dismiss } from "./EditDrawer";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import type { Translate } from "../i18n/translate";
import { useFormat } from "../i18n/format";
import {
  pmsOccupancyRefusal,
  useOccupancySource,
} from "../hooks/useOccupancySource";
import {
  checkGuest,
  useRoomMapping,
  type PmsUnit,
  type RoomMapping,
} from "../hooks/useRoomMapping";
import { PermissionGate, usePermission } from "../auth/access";
import type { PremiseCommit } from "../context/PremiseWorkspace";
import { FONT_MONO } from "../theme";

interface Props {
  rooms: Unit[];
  /**
   * Persist a change to the premise's rooms NOW and toast it (the workspace's
   * `commit`). Adding, editing and deleting a room each write straight away;
   * there is no draft and no page-level Save.
   */
  onCommit: PremiseCommit;
  /**
   * Called after the server accepted an occupancy change made through its own
   * endpoint, so the workspace's copy of the rooms matches without looking
   * "unsaved". The change is not written again.
   */
  onServerChange?: (update: (c: PremiseConfig) => PremiseConfig) => void;
  /**
   * When set, occupancy toggles + bulk check-in/out call the server
   * directly (immediate persist). Without it the column is hidden - used
   * by tests / new-premise creation flows where there's no slug yet.
   */
  premiseId?: string;
  /**
   * When "care", occupancy is read-only (admission owns it). Family photos
   * are managed per RESIDENT on the Residents tab, not per room.
   */
  segment?: "hotel" | "care";
  /**
   * The premise's products (licence product keys). Decides which of the fixed
   * room types the Type picker offers - see `roomTypesForPremise`.
   */
  premiseProducts?: Iterable<string>;
  /**
   * Nenda staff: may mark a unit as a demo unit (UNIT-5). The server strips
   * the flag from anyone else's save; this only decides what is offered.
   */
  staff?: boolean;
}

type Palette = { id: string; name: string; rooms: number }[];

const LIST_SEPARATOR = ", ";

export default function RoomsEditor({
  rooms,
  onCommit,
  onServerChange,
  premiseId,
  segment,
  premiseProducts,
  staff = false,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const [roomTypes, setRoomTypes] = useState<RoomTypeRecord[]>([]);
  const hasFrontlayer = [...(premiseProducts ?? [])].includes("frontlayer");
  const allowedTypes = roomTypesForPremise({
    products: premiseProducts ?? [],
    segment,
  });
  /** The PALETTE: what this premise licensed. null while loading. */
  const [palette, setPalette] = useState<Palette | null>(null);
  /** What each room is assigned, keyed by room number. */
  const [assigned, setAssigned] = useState<Map<string, string[]>>(new Map());
  /** The room awaiting delete confirmation, by id. */
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  /** Which product a bulk assign is currently writing, if any. */
  const [bulkProduct, setBulkProduct] = useState<string | null>(null);
  /** The "Add rooms" modal (create). */
  const [addOpen, setAddOpen] = useState(false);
  /**
   * The room open in the edit drawer, by index. The drawer is mounted only
   * while this is set; it slides out first and then clears it via onClose.
   */
  const [editIdx, setEditIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!premiseId) return;
    let alive = true;
    void foyerApi
      .premiseEntitlement(premiseId)
      .then((r) => {
        if (!alive) return;
        setPalette(r.featureSets);
        setAssigned(new Map(r.rooms.map((x) => [x.roomNumber, x.products])));
      })
      // An empty palette rather than a broken panel: the rooms table is the
      // point of this screen and must not be held hostage by what sits above.
      .catch(() => {
        if (alive) setPalette([]);
      });
    return () => {
      alive = false;
    };
  }, [premiseId]);
  const [occupancyBusy, setOccupancyBusy] = useState<Set<string>>(new Set());
  // Rooms with an active guest-companion token - a lightweight indicator only.
  // Companion sessions are never Fleet devices or rooms-list rows.
  const [companionRooms, setCompanionRooms] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // On a care premise occupancy is DERIVED: admitting a resident fills the
  // room, discharging empties it. Hand-toggling it here would desync the flag
  // from the Occupancy record - the server refuses it with 409 - so the column
  // is read-only and points at the flow that actually owns it.
  const occupancyDerived = segment === "care";
  // A PMS that owns check-ins: the switch asks it, or is read-only.
  const { driver } = useOccupancySource(premiseId);
  // Which PMS room each unit is. The hotel fixes its own mapping (Sigge,
  // 2026-10-28): it is gated on `units`, not on Nenda's `pms-link`.
  const canSeePms = usePermission("units", "view");
  const pms = useRoomMapping(canSeePms ? premiseId : undefined);
  const [acceptBusy, setAcceptBusy] = useState(false);

  useEffect(() => {
    foyerApi
      .listRoomTypes()
      .then((list) => setRoomTypes(Array.isArray(list) ? list : []))
      .catch(() => setRoomTypes([]));
  }, []);

  // Which rooms have an active guest-companion token (indicator badge only).
  useEffect(() => {
    if (!premiseId) return;
    foyerApi
      .guestCompanionRooms(premiseId)
      .then((res) => {
        setCompanionRooms(res.enabled ? new Set(res.roomIds) : new Set());
      })
      .catch(() => {
        /* non-fatal: badge just won't show */
      });
  }, [premiseId]);

  /**
   * Save one room from its drawer: its fields and, if they changed, its
   * content - as ONE Save. Content goes first because it is keyed by the
   * room's current number; the rename travels with the fields afterwards.
   * Resolves true when everything asked for is stored, so the drawer closes
   * only then and keeps what the user typed otherwise.
   */
  async function saveRoom(
    original: Unit,
    patch: Partial<Unit>,
    products: string[] | null,
    /** The PMS unit picked in the drawer; undefined leaves the mapping alone. */
    unitRef?: string | null
  ): Promise<boolean> {
    const nextId = (patch.id ?? original.id).trim();
    // "" and undefined both mean "not set" (the drawer sends "" to clear a
    // venue), so an untouched empty field is not a change.
    // null is how orientation and panel count say "not set".
    const norm = (v: unknown) => (v === "" || v === null ? undefined : v);
    const fieldsChanged = (Object.keys(patch) as (keyof Unit)[]).some(
      (k) => norm(patch[k]) !== norm(original[k])
    );
    let contentSaved = false;
    if (products && premiseId) {
      try {
        await foyerApi.assignRoomProducts(premiseId, [
          { roomNumber: original.id, products },
        ]);
        const fresh = await foyerApi.premiseEntitlement(premiseId);
        setPalette(fresh.featureSets);
        setAssigned(
          new Map(fresh.rooms.map((x) => [x.roomNumber, x.products]))
        );
        contentSaved = true;
      } catch (e) {
        toast.error(
          describeError(t, "updateUnitContent", e, { room: original.id })
        );
        return false;
      }
    }
    const renamed = nextId !== original.id;
    if (fieldsChanged) {
      const wasMapped =
        renamed &&
        ["mapped", "stale"].includes(pms.rowFor(original.id)?.state ?? "");
      const ok = await onCommit(
        (c) => ({
          ...c,
          rooms: c.rooms.map((r) =>
            r.id === original.id
              ? {
                  ...r,
                  ...patch,
                  id: nextId,
                  // The server renames the unit in place, so its schedule,
                  // groups and screens go with it (UNIT-3).
                  ...(renamed ? { renamedFrom: original.id } : {}),
                }
              : r
          ),
        }),
        {
          success: renamed
            ? wasMapped && !unitRef && pms.connection
              ? t("roomsEditor.renamedUnmapped", {
                  room: original.id,
                  to: nextId,
                  vendor: pms.connection.displayName,
                })
              : t("roomsEditor.renamed", { room: original.id, to: nextId })
            : contentSaved
              ? t("roomsEditor.updatedWithContent", { room: original.id })
              : t("roomsEditor.updated", { room: original.id }),
          failure: "saveUnit",
          failureParams: { room: original.id },
        }
      );
      if (!ok) return false;
      // The server dropped the old number's mapping with the rename.
      if (renamed && wasMapped) await pms.reload();
    }
    // The mapping goes last, under the unit's number after this Save: a
    // rename drops the old mapping on the server, so the room picked in the
    // same Save is stored for the new number.
    if (unitRef !== undefined && premiseId && pms.connection) {
      const vendor = pms.connection.displayName;
      try {
        await foyerApi.setLinkRoomMappings(premiseId, [
          { roomId: nextId, unitRef },
        ]);
        const unit = pms.units.find((u) => u.unitRef === unitRef);
        toast.success(
          unit
            ? t("roomsEditor.mappedTo", {
                room: nextId,
                vendor,
                label: pms.labelOf(unit),
              })
            : t("roomsEditor.unmappedFrom", { room: nextId, vendor })
        );
        await pms.reload();
      } catch (e) {
        toast.error(
          describeError(t, "mapUnitToPms", e, { room: nextId, vendor })
        );
        // A unit already renamed has no drawer to come back to.
        return fieldsChanged;
      }
    } else if (!fieldsChanged && contentSaved) {
      toast.success(t("roomsEditor.contentUpdated", { room: original.id }));
    }
    return true;
  }

  /** Add rooms. Rooms that exist by the time the write runs are skipped. */
  function addRoomsNow(additions: Unit[]): Promise<boolean> {
    const first = additions[0]!.id;
    const last = additions[additions.length - 1]!.id;
    return onCommit(
      (c) => {
        const have = new Set(c.rooms.map((r) => r.id));
        return {
          ...c,
          rooms: [...c.rooms, ...additions.filter((r) => !have.has(r.id))],
        };
      },
      {
        success:
          additions.length === 1
            ? t("roomsEditor.added", { room: first })
            : t("roomsEditor.addedRange", {
                count: additions.length,
                first,
                last,
              }),
        failure: additions.length === 1 ? "addUnit" : "addUnitRange",
        failureParams:
          additions.length === 1
            ? { room: first }
            : { count: additions.length, first, last },
      }
    );
  }

  async function confirmDelete() {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteBusy(true);
    const ok = await onCommit(
      (c) => ({ ...c, rooms: c.rooms.filter((r) => r.id !== id) }),
      {
        success: t("roomsEditor.deleted", { room: id }),
        failure: "deleteUnit",
        failureParams: { room: id },
      }
    );
    setDeleteBusy(false);
    if (ok) {
      setDeleteId(null);
      // The unit's PMS mapping went with it, on the server.
      if (pms.connection) await pms.reload();
    }
  }

  async function setOccupancy(idx: number, occupied: boolean) {
    const room = rooms[idx];
    if (!premiseId) return;
    setOccupancyBusy((s) => new Set(s).add(room.id));
    try {
      await foyerApi.setRoomOccupancy(premiseId, room.id, occupied);
      onServerChange?.((c) => ({
        ...c,
        rooms: c.rooms.map((r) => (r.id === room.id ? { ...r, occupied } : r)),
      }));
      toast.success(
        driver
          ? t(
              occupied ? "roomsEditor.checkedInIn" : "roomsEditor.checkedOutIn",
              {
                room: room.id,
                vendor: driver.displayName,
              }
            )
          : occupied
            ? t("roomsEditor.checkedIn", { room: room.id })
            : t("roomsEditor.checkedOut", { room: room.id })
      );
    } catch (e) {
      // Leave the prior state and say so - a silent failure here looks like
      // a stuck switch.
      toast.error(
        pmsOccupancyRefusal(t, e, room.id, occupied) ??
          describeError(t, occupied ? "checkInUnit" : "checkOutUnit", e, {
            room: room.id,
          })
      );
    } finally {
      setOccupancyBusy((s) => {
        const c = new Set(s);
        c.delete(room.id);
        return c;
      });
    }
  }

  async function bulkOccupancy(occupied: boolean) {
    if (!premiseId) return;
    setBulkBusy(true);
    try {
      if (occupied) await foyerApi.checkInAll(premiseId);
      else await foyerApi.checkOutAll(premiseId);
      onServerChange?.((c) => ({
        ...c,
        rooms: c.rooms.map((r) => ({ ...r, occupied })),
      }));
      toast.success(
        occupied
          ? t("roomsEditor.allCheckedIn", { count: rooms.length })
          : t("roomsEditor.allCheckedOut", { count: rooms.length })
      );
    } catch (e) {
      toast.error(
        describeError(t, occupied ? "checkInAllUnits" : "checkOutAllUnits", e)
      );
    } finally {
      setBulkBusy(false);
    }
  }

  // The fixed types' names are known here, so a room shows "Hotel room" even
  // before (or without) the catalogue list arriving.
  const typeNameById = new Map<string, string>([
    ...BUILTIN_ROOM_TYPES.map((t) => [t.id, t.name] as [string, string]),
    ...roomTypes.map((t) => [t.id, t.name] as [string, string]),
  ]);
  const productNameById = new Map((palette ?? []).map((f) => [f.id, f.name]));

  /**
   * Give one product to every room, or take it off every room.
   *
   * 🚨 A UNION, not a replacement. Each room's payload is its existing
   * assignment plus (or minus) this one product — sending only the product
   * would silently strip everything else those rooms hold, which is the
   * full-replacement mistake this model already made once with room types.
   *
   * Rooms that already agree are still sent. The server writes the same value
   * and recompiles once, and filtering them out here would mean maintaining a
   * second opinion about what "already correct" means.
   */
  async function bulkAssign(productId: string, give: boolean) {
    if (!premiseId) return;
    setBulkProduct(productId);
    try {
      const assignments = rooms.map((r) => {
        const current = assigned.get(r.id) ?? [];
        const next = give
          ? [...new Set([...current, productId])]
          : current.filter((id) => id !== productId);
        return { roomNumber: r.id, products: next.sort() };
      });
      await foyerApi.assignRoomProducts(premiseId, assignments);
      const fresh = await foyerApi.premiseEntitlement(premiseId);
      setPalette(fresh.featureSets);
      setAssigned(new Map(fresh.rooms.map((x) => [x.roomNumber, x.products])));
      const name = productNameById.get(productId) ?? productId;
      toast.success(
        give
          ? t("roomsEditor.givenToAll", { name, count: rooms.length })
          : t("roomsEditor.takenOffAll", { name, count: rooms.length })
      );
    } catch (e) {
      const name = productNameById.get(productId) ?? productId;
      toast.error(
        describeError(
          t,
          give ? "giveProductToAllUnits" : "takeProductOffAllUnits",
          e,
          { product: name }
        )
      );
    } finally {
      setBulkProduct(null);
    }
  }

  /** Map every unit whose number is exactly one PMS room's name. A suggestion
   *  is never a mapping until somebody accepts it, here or in the drawer. */
  async function acceptSuggestions() {
    if (!premiseId || !pms.connection) return;
    const proposed = pms.proposed;
    setAcceptBusy(true);
    try {
      await foyerApi.setLinkRoomMappings(
        premiseId,
        proposed.map((r) => ({ roomId: r.roomId, unitRef: r.unitRef }))
      );
      toast.success(
        t("roomsEditor.pms.mappedUnits", {
          count: proposed.length,
          vendor: pms.connection.displayName,
        })
      );
      await pms.reload();
    } catch (e) {
      toast.error(
        describeError(t, "mapUnitsToPms", e, {
          vendor: pms.connection.displayName,
        })
      );
    } finally {
      setAcceptBusy(false);
    }
  }

  const editRoom = editIdx !== null ? rooms[editIdx] : undefined;

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Typography variant="h6">{t("roomsEditor.title")}</Typography>
        <Stack direction="row" spacing={1}>
          {premiseId && rooms.length > 0 && !occupancyDerived && !driver && (
            <PermissionGate resource="units" level="edit">
              <Button
                size="small"
                variant="outlined"
                disabled={bulkBusy}
                onClick={() => bulkOccupancy(true)}
                data-testid="rooms-checkin-all"
              >
                {t("roomsEditor.checkInAll")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={bulkBusy}
                onClick={() => bulkOccupancy(false)}
                data-testid="rooms-checkout-all"
              >
                {t("roomsEditor.checkOutAll")}
              </Button>
            </PermissionGate>
          )}
          <PermissionGate resource="units" level="administrate">
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
              data-testid="rooms-add-open"
            >
              {t("roomsEditor.addUnits")}
            </Button>
          </PermissionGate>
        </Stack>
      </Stack>
      {/**
       * What these rooms PLAY — shown once, not per row.
       *
       * Entitlement is a fact about the PREMISE now: every room plays what the
       * customer bought, so repeating it on 200 rows would be 200 copies of
       * one sentence. Reported by Sigge, who added a product to a premise and
       * then could not find it anywhere near the rooms — the CMS showed a
       * room's number, type and occupancy, and nothing about content.
       *
       * Read from the server's own compiler, so it cannot claim something the
       * rooms do not hold.
       */}
      {premiseId && (
        <Alert
          severity={palette === null || palette.length ? "info" : "warning"}
          sx={{ mb: 2 }}
          data-testid="rooms-entitlement"
        >
          {palette === null ? (
            t("roomsEditor.checkingLicence")
          ) : palette.length ? (
            <>
              {t("roomsEditor.licensedHere")}{" "}
              {palette.map((f, i) => (
                <span key={f.id}>
                  {i > 0 ? LIST_SEPARATOR : ""}
                  <strong>{f.name}</strong>{" "}
                  <Typography component="span" variant="caption">
                    {t("roomsEditor.licensedUnits", { count: f.rooms })}
                  </Typography>
                </span>
              ))}
              {t("roomsEditor.licensedEnd")}{" "}
              {/* The action belongs to a PRODUCT, not to this sentence.
                  "Click here for bulk" reads fine with one product and makes
                  somebody guess with three — so the link opens a dialog with a
                  row per product, which reads the same at any number. */}
              <PermissionGate resource="units" level="administrate">
                <Trans
                  t={t}
                  i18nKey="roomsEditor.assignInBulkHint"
                  components={{
                    link: (
                      <Link
                        component="button"
                        type="button"
                        onClick={() => setBulkOpen(true)}
                        data-testid="rooms-bulk-open"
                      />
                    ),
                  }}
                />
              </PermissionGate>
            </>
          ) : (
            t("roomsEditor.nothingLicensedYet")
          )}
        </Alert>
      )}

      {pms.connection && pms.problem && (
        <Alert severity="warning" data-testid="rooms-pms-problem">
          {t("roomsEditor.pms.readFailed", {
            vendor: pms.connection.displayName,
            problem: pms.problem,
          })}
        </Alert>
      )}
      {pms.connection && pms.ok && pms.proposed.length > 0 && (
        <Alert
          severity="info"
          data-testid="rooms-pms-suggestions"
          action={
            <PermissionGate resource="units" level="edit">
              <Button
                size="small"
                color="inherit"
                disabled={acceptBusy}
                onClick={() => void acceptSuggestions()}
                data-testid="rooms-pms-accept"
              >
                {t("roomsEditor.pms.accept", { count: pms.proposed.length })}
              </Button>
            </PermissionGate>
          }
        >
          {t("roomsEditor.pms.suggestions", {
            count: pms.proposed.length,
            vendor: pms.connection.displayName,
          })}
        </Alert>
      )}

      {/* Read-only rows: a room is edited in its drawer (row click or the
          pencil). Rooms tables run to hundreds of rows, so a row is plain
          text plus the occupancy switch - no inputs per cell. */}
      {rooms.length > 0 && (
        <TableContainer>
          <Table size="small" data-testid="rooms-list" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                {/* No separate Name (Sigge, 2026-10-19): the room "number" is
                    free text, so a room that needs a name is called that
                    ("Receptionen"). Type takes the slack and never wraps. */}
                <TableCell sx={{ width: 200 }}>
                  {t("roomsEditor.unit")}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {t("common:labels.type")}
                </TableCell>
                {premiseId && (
                  <TableCell sx={{ width: 220 }}>
                    {t("common:labels.content")}
                  </TableCell>
                )}
                {pms.connection && (
                  <TableCell sx={{ width: 180 }}>
                    <Tooltip
                      title={t("roomsEditor.pms.columnHint", {
                        vendor: pms.connection.displayName,
                      })}
                    >
                      <span
                        data-testid="rooms-pms-header"
                        style={{ textDecoration: "underline dotted" }}
                      >
                        {pms.connection.displayName}
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
                {premiseId && (
                  <TableCell sx={{ width: 90 }} align="center">
                    {driver ? (
                      <Tooltip
                        title={
                          driver.canWrite
                            ? t("roomsEditor.occupiedFollowsWrite", {
                                vendor: driver.displayName,
                              })
                            : t("roomsEditor.occupiedFollowsRead", {
                                vendor: driver.displayName,
                              })
                        }
                      >
                        <span
                          data-testid="rooms-occupied-source"
                          style={{ textDecoration: "underline dotted" }}
                        >
                          {t("roomsEditor.occupied")}
                        </span>
                      </Tooltip>
                    ) : (
                      t("roomsEditor.occupied")
                    )}
                  </TableCell>
                )}
                <TableCell sx={{ width: 148 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room, i) => {
                const products = assigned.get(room.id) ?? [];
                return (
                  <TableRow
                    key={room.id}
                    data-testid={`room-row-${room.id}`}
                    hover
                    onClick={() => setEditIdx(i)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "fontWeightMedium" }}
                        >
                          {room.id}
                        </Typography>
                        {room.isDemo && (
                          <Tooltip title={t("roomsEditor.demoTooltip")}>
                            <Chip
                              size="small"
                              label={t("roomsEditor.demo")}
                              color="warning"
                              variant="outlined"
                              sx={{
                                height: 18,
                                typography: "micro",
                                fontWeight: "fontWeightSemibold",
                              }}
                              data-testid={`room-demo-${room.id}`}
                            />
                          </Tooltip>
                        )}
                        {companionRooms.has(room.id) && (
                          <Tooltip title={t("roomsEditor.companionActive")}>
                            <PhoneIphoneIcon
                              fontSize="small"
                              color="primary"
                              data-testid={`room-companion-${room.id}`}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {room.roomType ? (
                        <Typography variant="body2">
                          {typeNameById.get(room.roomType) ??
                            t("roomsEditor.unknownType")}
                          {room.roomType === DOOR_SIGN_ROOM_TYPE ? (
                            <Typography
                              component="span"
                              variant="caption"
                              color={
                                room.doorSignFor ? "text.secondary" : "error"
                              }
                              sx={{ ml: 1 }}
                              data-testid={`room-door-sign-for-${room.id}`}
                            >
                              {room.doorSignFor
                                ? t("roomsEditor.doorSignFor", {
                                    room: room.doorSignFor,
                                  })
                                : t("roomsEditor.servesNoUnit")}
                            </Typography>
                          ) : null}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          {t("roomsEditor.untyped")}
                        </Typography>
                      )}
                    </TableCell>
                    {premiseId && (
                      <TableCell data-testid={`room-content-${room.id}`}>
                        <Typography
                          variant="body2"
                          color={
                            products.length ? "text.primary" : "text.secondary"
                          }
                        >
                          {!palette?.length
                            ? t("roomsEditor.nothingLicensed")
                            : products.length === 0
                              ? t("roomsEditor.noProducts")
                              : products
                                  .map((id) => productNameById.get(id) ?? id)
                                  .join(LIST_SEPARATOR)}
                        </Typography>
                      </TableCell>
                    )}
                    {pms.connection && (
                      <TableCell data-testid={`room-pms-${room.id}`}>
                        <PmsCell pms={pms} roomId={room.id} />
                      </TableCell>
                    )}
                    {premiseId && (
                      <TableCell
                        sx={{ py: 0.5 }}
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <PermissionGate
                          resource="units"
                          level="edit"
                          deniedProps={{ disabled: true }}
                        >
                          <Switch
                            size="small"
                            checked={!!room.occupied}
                            disabled={
                              occupancyDerived ||
                              (driver !== null && !driver.canWrite) ||
                              occupancyBusy.has(room.id)
                            }
                            onChange={(e) => setOccupancy(i, e.target.checked)}
                            inputProps={
                              {
                                "aria-label": t("roomsEditor.occupiedAria", {
                                  room: room.id,
                                }),
                                "data-testid": `room-occupied-${room.id}`,
                              } as React.InputHTMLAttributes<HTMLInputElement>
                            }
                          />
                        </PermissionGate>
                      </TableCell>
                    )}
                    <TableCell
                      align="right"
                      sx={{ py: 0.5, whiteSpace: "nowrap" }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        {roomHasIntegrationToken(room) && (
                          <Tooltip title={t("roomsEditor.integrationTokenSet")}>
                            <KeyIcon
                              fontSize="small"
                              color="success"
                              data-testid={`room-has-integration-${room.id}`}
                            />
                          </Tooltip>
                        )}
                        <PermissionGate resource="units" level="administrate">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditIdx(i);
                            }}
                            data-testid={`room-edit-${room.id}`}
                            aria-label={t("roomsEditor.editUnit", {
                              room: room.id,
                            })}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteId(room.id);
                            }}
                            data-testid={`delete-room-${room.id}`}
                            aria-label={t("roomsEditor.deleteUnit", {
                              room: room.id,
                            })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </PermissionGate>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/**
       * Bulk assignment, a ROW PER PRODUCT.
       *
       * Sigge asked for "click here for bulk" in the summary line and then
       * asked the right follow-up: what does that copy say when there are
       * several products? Nothing good — "bulk" is meaningless without naming
       * one, and a sentence cannot name three. A row per product reads the
       * same at any number, and leaves somewhere for "give it to THESE rooms"
       * to live later without another redesign.
       */}
      <Dialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("roomsEditor.bulkTitle")}
          <Typography variant="body2" color="text.secondary">
            {t("roomsEditor.bulkSubtitle")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {(palette ?? []).map((f) => (
              <Stack
                key={f.id}
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2" fontWeight="fontWeightSemibold">
                    {f.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("roomsEditor.bulkCount", {
                      assigned: f.rooms,
                      count: rooms.length,
                    })}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={bulkProduct !== null || f.rooms === rooms.length}
                    onClick={() => void bulkAssign(f.id, true)}
                    data-testid={`bulk-all-${f.id}`}
                  >
                    {t("roomsEditor.giveToAll")}
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={bulkProduct !== null || f.rooms === 0}
                    onClick={() => void bulkAssign(f.id, false)}
                    data-testid={`bulk-none-${f.id}`}
                  >
                    {t("roomsEditor.takeOffAll")}
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>
            {t("common:buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {rooms.length === 0 && (
        <Typography color="text.secondary" data-testid="empty-rooms">
          {t("roomsEditor.empty")}
        </Typography>
      )}

      {addOpen && (
        <AddRoomsDialog
          rooms={rooms}
          allowedTypes={allowedTypes}
          typeNameById={typeNameById}
          onAdd={addRoomsNow}
          onClose={() => setAddOpen(false)}
        />
      )}

      {editRoom && editIdx !== null && (
        <RoomDrawer
          // A fresh draft per room opened.
          key={`${editIdx}`}
          room={editRoom}
          rooms={rooms}
          hasFrontlayer={hasFrontlayer}
          allowedTypes={allowedTypes}
          typeNameById={typeNameById}
          premiseId={premiseId}
          palette={palette}
          staff={staff}
          products={assigned.get(editRoom.id) ?? []}
          pms={pms}
          onSave={(patch, products, unitRef) =>
            saveRoom(editRoom, patch, products, unitRef)
          }
          onClose={() => setEditIdx(null)}
        />
      )}

      <Dialog
        open={deleteId !== null}
        onClose={deleteBusy ? undefined : () => setDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("roomsEditor.deleteTitle", { room: deleteId })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("roomsEditor.deleteBody", { room: deleteId })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={deleteBusy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteBusy}
            onClick={() => void confirmDelete()}
            data-testid="room-delete-confirm"
          >
            {deleteBusy
              ? t("roomsEditor.deleting")
              : t("common:buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * The Type picker's options: the fixed room types this premise's products
 * allow (Sigge, 2026-10-19). A room still holding an older catalogue type
 * shows it as a disabled "current" entry, so opening the drawer neither hides
 * nor silently changes it; picking a fixed type replaces it.
 */
function roomTypeOptions(
  t: Translate,
  allowed: BuiltinRoomType[],
  current: string,
  typeNameById: Map<string, string>
) {
  const items = allowed.map((t) => (
    <MenuItem key={t.id} value={t.id}>
      {t.name}
    </MenuItem>
  ));
  if (!current || allowed.some((t) => t.id === current)) return items;
  return [
    <MenuItem key={`current-${current}`} value={current} disabled>
      {t("roomsEditor.currentNoLongerOffered", {
        name: typeNameById.get(current) ?? t("roomsEditor.unknownType"),
      })}
    </MenuItem>,
    ...items,
  ];
}

/** "Add rooms": a range of numbers plus an optional type, in a modal. */
function AddRoomsDialog({
  rooms,
  allowedTypes,
  typeNameById,
  onAdd,
  onClose,
}: {
  rooms: Unit[];
  allowedTypes: BuiltinRoomType[];
  typeNameById: Map<string, string>;
  onAdd: (additions: Unit[]) => Promise<boolean>;
  onClose: () => void;
}) {
  const { t } = useTranslation(["premises", "common"]);
  const [rangeText, setRangeText] = useState("");
  const [pickedType, setPickedType] = useState<string>("");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // The footer hands out `dismiss`; Enter in the range field needs it too.
  const dismissRef = useRef<Dismiss | null>(null);

  async function addRooms() {
    if (adding) return;
    const parsed = parseRange(rangeText, t);
    if (parsed.error) {
      setAddError(parsed.error);
      return;
    }
    setAddError(null);
    const existing = new Set(rooms.map((r) => r.id));
    // Door signs: the range names the rooms they hang outside, and each sign
    // becomes its own row "<room>-door" serving that room.
    if (pickedType === DOOR_SIGN_ROOM_TYPE) {
      const unknown = parsed.ids.filter((id) => !existing.has(id));
      if (unknown.length) {
        setAddError(
          t("roomsEditor.addFirst", {
            count: unknown.length,
            rooms: unknown.slice(0, 5).join(LIST_SEPARATOR),
          })
        );
        return;
      }
      const signs: Unit[] = parsed.ids
        .map((id) => ({
          id: `${id}-door`,
          roomType: DOOR_SIGN_ROOM_TYPE,
          doorSignFor: id,
        }))
        .filter((r) => !existing.has(r.id));
      if (signs.length === 0) {
        setAddError(t("roomsEditor.alreadyHaveDoorSign"));
        return;
      }
      await submit(signs);
      return;
    }
    const additions: Unit[] = parsed.ids
      .filter((id) => !existing.has(id))
      .map((id) => ({
        id,
        ...(pickedType ? { roomType: pickedType } : {}),
      }));
    if (additions.length === 0) {
      setAddError(t("roomsEditor.rangeExists"));
      return;
    }
    await submit(additions);
  }

  async function submit(additions: Unit[]) {
    setAdding(true);
    const ok = await onAdd(additions);
    setAdding(false);
    if (ok) dismissRef.current?.();
  }

  return (
    <ItemFormSurface
      mode="create"
      onClose={onClose}
      title={t("roomsEditor.addUnits")}
      subtitle={t("roomsEditor.addSubtitle")}
      data-testid="rooms-add-dialog"
      actions={(dismiss) => {
        dismissRef.current = dismiss;
        return (
          <>
            <Button onClick={() => dismiss()} disabled={adding}>
              {t("common:buttons.cancel")}
            </Button>
            <Button
              startIcon={<AddIcon />}
              onClick={() => void addRooms()}
              disabled={adding}
              variant="contained"
              data-testid="add-room"
            >
              {adding ? t("roomsEditor.adding") : t("common:buttons.add")}
            </Button>
          </>
        );
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label={t("roomsEditor.title")}
          autoFocus
          value={rangeText}
          onChange={(e) => {
            setRangeText(e.target.value);
            if (addError) setAddError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void addRooms();
            }
          }}
          size="small"
          placeholder={t("roomsEditor.rangePlaceholder")}
          helperText={
            addError ??
            (pickedType === DOOR_SIGN_ROOM_TYPE
              ? t("roomsEditor.doorSignRangeHelp")
              : t("roomsEditor.rangeHelp"))
          }
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "new-room-id" }}
          error={Boolean(addError)}
        />
        <TextField
          select
          label={t("common:labels.type")}
          value={pickedType}
          onChange={(e) => setPickedType(e.target.value)}
          size="small"
          fullWidth
          SelectProps={{
            displayEmpty: true,
            renderValue: (val) => {
              if (!val)
                return (
                  <Typography variant="body2" color="text.secondary">
                    {t("roomsEditor.untyped")}
                  </Typography>
                );
              return (
                typeNameById.get(val as string) ?? t("roomsEditor.unknownType")
              );
            },
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "new-room-type" }}
        >
          <MenuItem value="">
            <em>{t("roomsEditor.untyped")}</em>
          </MenuItem>
          {roomTypeOptions(t, allowedTypes, "", typeNameById)}
        </TextField>
      </Stack>
    </ItemFormSurface>
  );
}

const INTEGRATION_TOKEN_PLACEHOLDER = "{integrationId}";

/** A titled block inside the room drawer. */
function DrawerSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={1.25}>
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "fontWeightSemibold" }}
        >
          {title}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Box>
      {children}
    </Stack>
  );
}

/** The PMS column: which PMS room this unit is, or that it is none. */
function PmsCell({ pms, roomId }: { pms: RoomMapping; roomId: string }) {
  const { t } = useTranslation(["premises", "common"]);
  if (!pms.ok) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("common:labels.emptyValue")}
      </Typography>
    );
  }
  const row = pms.rowFor(roomId);
  const unit = pms.units.find((u) => u.unitRef === row?.unitRef);
  switch (row?.state) {
    case "mapped":
      return (
        <Typography variant="body2">
          {unit ? pms.labelOf(unit) : row.unitName}
        </Typography>
      );
    case "stale":
      return (
        <Chip
          size="small"
          color="warning"
          label={t("roomsEditor.pms.gone", {
            vendor: pms.connection?.displayName ?? t("occupancySource.thePms"),
          })}
        />
      );
    case "proposed":
      return (
        <Typography variant="body2" color="text.secondary">
          {t("roomsEditor.pms.notMapped")}{" "}
          <Typography component="span" variant="caption">
            {t("roomsEditor.pms.suggested", {
              name: unit ? pms.labelOf(unit) : row.unitName,
            })}
          </Typography>
        </Typography>
      );
    default:
      return (
        <Chip
          size="small"
          variant="outlined"
          label={t("roomsEditor.pms.notMapped")}
          data-testid={`room-pms-unmapped-${roomId}`}
        />
      );
  }
}

/** The drawer's mapping field: pick the PMS room, take a suggestion, check
 *  who a charge would reach. Saved with the unit. */
function PmsField({
  pms,
  roomId,
  row,
  unitRef,
  savedUnitRef,
  dropped,
  onChange,
  disabled,
  guest,
  onCheckGuest,
}: {
  pms: RoomMapping;
  roomId: string;
  row: ReturnType<RoomMapping["rowFor"]>;
  unitRef: string | null;
  savedUnitRef: string | null;
  /** The room a rename in this draft unmaps the unit from. */
  dropped: PmsUnit | null;
  onChange: (unitRef: string | null) => void;
  disabled: boolean;
  guest: string | null;
  onCheckGuest: () => void;
}) {
  const { t } = useTranslation(["premises", "common"]);
  const vendor = pms.connection?.displayName ?? t("occupancySource.thePms");
  if (!pms.ok) {
    return (
      <Alert severity="warning" data-testid={`room-pms-problem-${roomId}`}>
        {t("roomsEditor.pms.readFailed", { vendor, problem: pms.problem })}
      </Alert>
    );
  }
  const current = pms.units.find((u) => u.unitRef === unitRef) ?? null;
  const suggestion =
    row?.state === "proposed" && unitRef === null
      ? (pms.units.find((u) => u.unitRef === row.unitRef) ?? null)
      : null;
  return (
    <Stack spacing={0.5}>
      <Autocomplete
        size="small"
        options={pms.units}
        value={current}
        disabled={disabled}
        getOptionLabel={pms.labelOf}
        isOptionEqualToValue={(a, b) => a.unitRef === b.unitRef}
        onChange={(_e, unit) => onChange(unit?.unitRef ?? null)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("roomsEditor.pms.roomIn", { vendor })}
            placeholder={t("roomsEditor.pms.notMapped")}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              ...params.inputProps,
              "data-testid": `room-pms-unit-${roomId}`,
            }}
          />
        )}
      />
      {dropped && (
        <Typography
          variant="caption"
          color="warning.main"
          data-testid={`room-pms-dropped-${roomId}`}
        >
          {t("roomsEditor.pms.dropped", {
            vendor,
            room: pms.labelOf(dropped),
          })}
        </Typography>
      )}
      {row?.state === "stale" && savedUnitRef && unitRef === savedUnitRef && (
        <Typography variant="caption" color="warning.main">
          {t("roomsEditor.pms.noLongerListed", { vendor })}
        </Typography>
      )}
      {suggestion && (
        <Typography variant="caption" color="text.secondary">
          {t("roomsEditor.pms.hasRoomWithName", { vendor })}{" "}
          <Link
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => onChange(suggestion.unitRef)}
            data-testid={`room-pms-use-suggestion-${roomId}`}
          >
            {t("roomsEditor.pms.useSuggestion", {
              name: pms.labelOf(suggestion),
            })}
          </Link>
        </Typography>
      )}
      {!suggestion && !dropped && row?.state !== "stale" && (
        <Typography variant="caption" color="text.secondary">
          {t("roomsEditor.pms.chargeHint", { vendor })}
        </Typography>
      )}
      {savedUnitRef && unitRef === savedUnitRef && row?.state === "mapped" && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            onClick={onCheckGuest}
            data-testid={`room-pms-check-${roomId}`}
          >
            {t("roomsEditor.pms.checkGuest")}
          </Button>
          {guest && (
            <Typography
              variant="caption"
              data-testid={`room-pms-guest-${roomId}`}
            >
              {guest}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * One room's editor, sliding in from the right.
 *
 * Everything here - number, type, content, integration token, venue - is a
 * draft written by the one Save, which toasts and closes the drawer. A failed
 * Save leaves the drawer open with the input intact.
 */
function RoomDrawer({
  room,
  rooms,
  hasFrontlayer,
  allowedTypes,
  typeNameById,
  premiseId,
  palette,
  staff,
  products: initialProducts,
  pms,
  onSave,
  onClose,
}: {
  room: Unit;
  /** The hotel's PMS rooms; `pms.connection` null shows no mapping field. */
  pms: RoomMapping;
  /** Nenda staff may change the demo flag (UNIT-5). */
  staff: boolean;
  /** Every room of the place - a door sign picks the one it serves. */
  rooms: Unit[];
  /** The place bought frontlayer: a hotel room may carry an integration token. */
  hasFrontlayer: boolean;
  allowedTypes: BuiltinRoomType[];
  typeNameById: Map<string, string>;
  premiseId?: string;
  palette: Palette | null;
  products: string[];
  /** Writes the fields; the content when `products` is non-null; the PMS
   *  mapping when `unitRef` is not undefined. */
  onSave: (
    patch: Partial<Unit>,
    products: string[] | null,
    unitRef?: string | null
  ) => Promise<boolean>;
  onClose: () => void;
}) {
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  // The room as it was when the drawer opened: its number keys the content
  // assignment, whatever the draft renames it to.
  const [original] = useState(room);
  const [id, setId] = useState(room.id);
  const [roomType, setRoomType] = useState(room.roomType ?? "");
  const [integrationId, setIntegrationId] = useState(room.integrationId ?? "");
  const [vistarVenueId, setVistarVenueId] = useState(room.vistarVenueId ?? "");
  const [doorSignFor, setDoorSignFor] = useState(room.doorSignFor ?? "");
  // UNIT-2. "" = not set: the unit follows what its screen reports.
  const [orientation, setOrientation] = useState<"" | "landscape" | "portrait">(
    room.orientation ?? ""
  );
  const [panelCount, setPanelCount] = useState(
    typeof room.panelCount === "number" ? String(room.panelCount) : ""
  );
  const [isDemo, setIsDemo] = useState(room.isDemo === true);
  // What the unit's screen reports, to prefill and to flag a mismatch.
  const [reported, setReported] = useState<string | null>(null);
  useEffect(() => {
    if (!premiseId) return;
    let alive = true;
    foyerApi
      .listScreens(premiseId)
      .then((list) => {
        const screen = list.find(
          (s) => s.roomId === room.id && s.live?.resolution
        );
        if (alive) setReported(screen?.live?.resolution ?? null);
      })
      .catch(() => {
        /* no screen facts: the field still works, just without the hint */
      });
    return () => {
      alive = false;
    };
  }, [premiseId, room.id]);
  const reportedOrientation = orientationOfResolution(reported);
  const mismatch = rotationMismatch(orientation || null, reported);
  const [products, setProducts] = useState<string[]>(initialProducts);
  const pmsRow = pms.rowFor(room.id);
  // Only a saved mapping is the starting value: a suggestion is offered, not
  // pre-filled, so a Save made for something else never accepts it.
  const savedUnitRef =
    pmsRow && (pmsRow.state === "mapped" || pmsRow.state === "stale")
      ? pmsRow.unitRef
      : null;
  const [unitRef, setUnitRef] = useState<string | null>(savedUnitRef);
  const [pickedUnitRef, setPickedUnitRef] = useState(false);
  const [guest, setGuest] = useState<string | null>(null);
  // The whole unit is Nenda's to save; its PMS room is the hotel's too
  // (Sigge, 2026-10-28), so a hotel admin's Save writes that and nothing else.
  const canSaveUnit = usePermission("units", "administrate");
  const canMapPms = usePermission("units", "edit");
  const [busy, setBusy] = useState(false);
  const trimmedId = id.trim();
  // A rename drops the unit's mapping (the server deletes it with the old
  // number): the renamed unit starts unmapped unless a room is picked now.
  const renamed = canSaveUnit && trimmedId !== "" && trimmedId !== room.id;
  const startUnitRef = renamed ? null : savedUnitRef;
  const draftUnitRef = renamed && !pickedUnitRef ? null : unitRef;
  const unitRefChange =
    draftUnitRef !== startUnitRef ? draftUnitRef : undefined;
  const droppedUnit =
    renamed && !pickedUnitRef && savedUnitRef
      ? (pms.units.find((u) => u.unitRef === savedUnitRef) ?? null)
      : null;
  const idTaken = rooms.some((r) => r.id === trimmedId && r.id !== room.id);
  const idProblem = !trimmedId
    ? t("roomsEditor.idRequired")
    : idTaken
      ? t("roomsEditor.idTaken", { room: trimmedId })
      : null;
  const productsChanged =
    products.length !== initialProducts.length ||
    products.some((p) => !initialProducts.includes(p));
  const isDoorSign = roomType === DOOR_SIGN_ROOM_TYPE;
  const showIntegration = hasFrontlayer && roomType === "builtin-hotel-room";
  // The rooms a door sign can hang outside: any other room that is not a
  // door sign itself.
  const servable = rooms.filter(
    (r) => r.id !== room.id && r.roomType !== DOOR_SIGN_ROOM_TYPE
  );
  /**
   * The Content menu, controlled so it CLOSES ON PICK. A `multiple` MUI
   * select stays open by design — you might want several — but the common
   * case is one product per room, and a menu that lingers over what you were
   * just looking at has to be dismissed before you can see what you did.
   * Reported by Sigge. Giving one room two products means opening it twice.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  const roomId = original.id;

  return (
    <EditDrawer
      open
      onClose={onClose}
      title={t("roomsEditor.drawerTitle", { room: original.id })}
      data-testid={`room-drawer-${roomId}`}
      busy={busy}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            data-testid="room-drawer-save"
            disabled={
              busy ||
              (canSaveUnit
                ? idProblem !== null
                : !pms.connection || !canMapPms || unitRefChange === undefined)
            }
            onClick={async () => {
              setBusy(true);
              let ok = false;
              try {
                ok = canSaveUnit
                  ? await onSave(
                      {
                        id: trimmedId,
                        roomType: roomType || undefined,
                        integrationId: integrationId.trim() || undefined,
                        // Sent even when empty: "" clears a stored venue.
                        vistarVenueId: vistarVenueId.trim(),
                        doorSignFor: isDoorSign
                          ? doorSignFor || undefined
                          : undefined,
                        // null clears a stored value; nothing to clear sends
                        // nothing.
                        orientation:
                          orientation || (room.orientation ? null : undefined),
                        panelCount:
                          panelCount.trim() !== ""
                            ? Number(panelCount)
                            : typeof room.panelCount === "number"
                              ? null
                              : undefined,
                        // Staff only, and only a change: the server keeps the
                        // stored flag when the field is absent.
                        isDemo:
                          staff && isDemo !== (room.isDemo === true)
                            ? isDemo
                            : undefined,
                      },
                      productsChanged ? products : null,
                      unitRefChange
                    )
                  : await onSave({}, null, unitRefChange);
              } finally {
                setBusy(false);
              }
              if (ok) dismiss();
            }}
          >
            {busy ? t("common:states.saving") : t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={3}>
        <DrawerSection title={t("roomsEditor.unit")}>
          <TextField
            label={t("roomsEditor.unit")}
            value={id}
            onChange={(e) => setId(e.target.value)}
            size="small"
            fullWidth
            error={idProblem !== null}
            helperText={idProblem ?? t("roomsEditor.idHelp")}
            inputProps={{ "data-testid": `room-id-${roomId}` }}
          />
          {pms.connection && (
            <PmsField
              pms={pms}
              roomId={roomId}
              // The row and its suggestion are for the number before a rename.
              row={renamed ? null : pmsRow}
              unitRef={draftUnitRef}
              savedUnitRef={startUnitRef}
              dropped={droppedUnit}
              onChange={(next) => {
                setUnitRef(next);
                setPickedUnitRef(true);
              }}
              disabled={busy || !canMapPms}
              guest={guest}
              onCheckGuest={async () => {
                if (!premiseId || !pms.connection) return;
                setGuest("…");
                setGuest(
                  await checkGuest(
                    t,
                    fmt,
                    premiseId,
                    roomId,
                    pms.connection.displayName
                  )
                );
              }}
            />
          )}
          <TextField
            select
            label={t("common:labels.type")}
            value={roomType}
            onChange={(e) => setRoomType(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (val) => {
                if (!val)
                  return (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontStyle="italic"
                    >
                      {t("roomsEditor.untyped")}
                    </Typography>
                  );
                return (
                  typeNameById.get(val as string) ??
                  t("roomsEditor.unknownType")
                );
              },
            }}
            inputProps={{ "data-testid": `room-type-${roomId}` }}
          >
            <MenuItem value="">
              <em>{t("roomsEditor.untyped")}</em>
            </MenuItem>
            {roomTypeOptions(
              t,
              allowedTypes,
              original.roomType ?? "",
              typeNameById
            )}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            {t("roomsEditor.typeHelp")}
          </Typography>
          {isDoorSign && (
            <TextField
              select
              label={t("roomsEditor.doorSignForLabel")}
              value={doorSignFor}
              onChange={(e) => setDoorSignFor(e.target.value)}
              size="small"
              fullWidth
              required
              error={doorSignFor === ""}
              helperText={t("roomsEditor.doorSignForHelp")}
              inputProps={{
                "data-testid": `room-door-sign-for-select-${roomId}`,
              }}
            >
              {servable.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.id}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DrawerSection>

        {premiseId && (
          <DrawerSection
            title={t("common:labels.content")}
            hint={t("roomsEditor.contentHint")}
          >
            {/* Only what the premise LICENSED is offered: a licence is a
                palette, and the server refuses anything else anyway.
                Offering more would be a checkbox that fails on save. */}
            <TextField
              select
              size="small"
              fullWidth
              disabled={!palette || palette.length === 0}
              value={products}
              onChange={(e) => {
                // MUI hands a multiple-select its value as an array, but
                // types it as the input's string. Autofill can still deliver
                // a comma-joined string, so both.
                const v = e.target.value as unknown as string | string[];
                setMenuOpen(false);
                setProducts(
                  typeof v === "string" ? v.split(",").filter(Boolean) : v
                );
              }}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                open: menuOpen,
                onOpen: () => setMenuOpen(true),
                onClose: () => setMenuOpen(false),
                renderValue: (v: unknown) => {
                  const ids = v as string[];
                  if (!palette?.length) return t("roomsEditor.nothingLicensed");
                  if (ids.length === 0) return t("roomsEditor.noProducts");
                  return ids
                    .map(
                      (pid) => palette.find((f) => f.id === pid)?.name ?? pid
                    )
                    .join(LIST_SEPARATOR);
                },
              }}
              inputProps={{ "data-testid": `room-products-${roomId}` }}
            >
              {(palette ?? []).map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </TextField>
          </DrawerSection>
        )}

        {/* The integration token is a hotel room's (Sigge, 2026-10-19):
            shown for a Hotel room on a place with frontlayer only. Signage
            rooms get the Vistar venue instead; a door sign gets neither. */}
        {showIntegration && (
          <DrawerSection
            title={t("roomsEditor.integrationToken")}
            hint={
              <Trans
                t={t}
                i18nKey="roomsEditor.integrationTokenHint"
                values={{ token: INTEGRATION_TOKEN_PLACEHOLDER }}
                components={{
                  code: (
                    <Box
                      component="code"
                      sx={{
                        fontFamily: "fontFamilyMono",
                        bgcolor: "action.hover",
                        px: 0.5,
                        borderRadius: 0.5,
                      }}
                    />
                  ),
                }}
              />
            }
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={integrationId}
                onChange={(e) => setIntegrationId(e.target.value)}
                size="small"
                fullWidth
                placeholder={t("roomsEditor.integrationTokenPlaceholder")}
                inputProps={{
                  "data-testid": `room-integration-id-${roomId}`,
                  style: { fontFamily: FONT_MONO },
                }}
              />
              {integrationId && (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setIntegrationId("")}
                  data-testid={`room-integration-clear-${roomId}`}
                >
                  {t("common:buttons.clear")}
                </Button>
              )}
            </Stack>
          </DrawerSection>
        )}

        {isSignageRoomType(roomType) && (
          <DrawerSection
            title={t("roomsEditor.orientation")}
            hint={t("roomsEditor.orientationHint")}
          >
            <TextField
              select
              value={orientation}
              onChange={(e) =>
                setOrientation(e.target.value as "" | "landscape" | "portrait")
              }
              size="small"
              fullWidth
              label={t("roomsEditor.orientation")}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "data-testid": `room-orientation-${roomId}` }}
            >
              <MenuItem value="">
                {reportedOrientation
                  ? t("roomsEditor.orientationNotSetReported", {
                      orientation: t(
                        `roomsEditor.orientationInline.${reportedOrientation}`
                      ),
                    })
                  : t("roomsEditor.orientationNotSet")}
              </MenuItem>
              <MenuItem value="landscape">
                {t("roomsEditor.orientationOption.landscape")}
              </MenuItem>
              <MenuItem value="portrait">
                {t("roomsEditor.orientationOption.portrait")}
              </MenuItem>
            </TextField>
            {mismatch && (
              <Alert
                severity="warning"
                data-testid={`room-orientation-mismatch-${roomId}`}
              >
                {t("roomsEditor.orientationMismatch", {
                  orientation: t(`roomsEditor.orientationInline.${mismatch}`),
                })}
              </Alert>
            )}
          </DrawerSection>
        )}

        {(staff || room.isDemo) && (
          <DrawerSection
            title={t("roomsEditor.demo")}
            hint={t("roomsEditor.demoHint")}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch
                checked={isDemo}
                disabled={!staff}
                onChange={(e) => setIsDemo(e.target.checked)}
                inputProps={
                  {
                    "aria-label": t("roomsEditor.demoUnit"),
                    "data-testid": `room-demo-switch-${roomId}`,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
              <Typography variant="body2">
                {t("roomsEditor.demoUnit")}
              </Typography>
            </Stack>
          </DrawerSection>
        )}

        {isSignageRoomType(roomType) && (
          <DrawerSection
            title={t("roomsEditor.advertising")}
            hint={t("roomsEditor.advertisingHint")}
          >
            <TextField
              value={vistarVenueId}
              onChange={(e) => setVistarVenueId(e.target.value)}
              size="small"
              fullWidth
              label={t("roomsEditor.vistarVenueId")}
              inputProps={{
                maxLength: 128,
                "data-testid": `room-vistar-venue-${roomId}`,
                style: { fontFamily: FONT_MONO },
              }}
            />
            <TextField
              value={panelCount}
              onChange={(e) => setPanelCount(e.target.value)}
              size="small"
              fullWidth
              type="number"
              label={t("roomsEditor.panels")}
              placeholder={t("roomsEditor.notSet")}
              helperText={t("roomsEditor.panelsHelp")}
              inputProps={{
                min: 1,
                max: 100,
                "data-testid": `room-panel-count-${roomId}`,
              }}
            />
          </DrawerSection>
        )}
      </Stack>
    </EditDrawer>
  );
}

/**
 * Parse a range expression into a list of room ids. Supports:
 *   - "101"                → ["101"]
 *   - "101-105"            → ["101","102","103","104","105"]
 *   - "1-5, 10, 21-22"     → ["1","2","3","4","5","10","21","22"]
 * Numbers in a range are zero-padded to match the width of the upper
 * bound when both endpoints have the same length ("001-009" → "001"..).
 */
function parseRange(
  text: string,
  t: Translate
): { ids: string[]; error: string | null } {
  const trimmed = text.trim();
  if (!trimmed) return { ids: [], error: t("roomsEditor.rangeEmpty") };
  const seen = new Set<string>();
  const out: string[] = [];
  for (const segmentRaw of trimmed.split(",")) {
    const segment = segmentRaw.trim();
    if (!segment) continue;
    const m = /^(\d+)\s*-\s*(\d+)$/.exec(segment);
    if (m) {
      const start = Number(m[1]);
      const end = Number(m[2]);
      if (start > end) {
        return {
          ids: [],
          error: t("roomsEditor.rangeReversed", { start, end }),
        };
      }
      if (end - start > 999) {
        return {
          ids: [],
          error: t("roomsEditor.rangeTooLarge"),
        };
      }
      const pad = m[1].length === m[2].length ? m[1].length : 0;
      for (let n = start; n <= end; n++) {
        const id = pad > 0 ? String(n).padStart(pad, "0") : String(n);
        if (!seen.has(id)) {
          seen.add(id);
          out.push(id);
        }
      }
    } else {
      // Plain id (digits or anything else - accept whatever the user types)
      if (!seen.has(segment)) {
        seen.add(segment);
        out.push(segment);
      }
    }
  }
  return { ids: out, error: null };
}

// `Chip` is imported above for future extension (room-type chip in
// table cells). Silence unused-import warning if tree-shaking trims it.
void Chip;
